/**
 * Scheduled table emails, for ANY DataSheet: the schedule stores where the table lives (the
 * page path with its view state, and which sheet on the page) instead of a frozen copy. At
 * send time the report worker opens that page headlessly as the schedule's owner, with a
 * capture job planted in sessionStorage; the page loads its own data the way it does for a
 * person, and `HeadlessCaptureAgent` photographs / serializes the named sheet through the
 * same capture handle the composer uses. No table needs a server-side twin.
 *
 * Every mounted DataSheet with email on registers here under its email name, so the agent
 * can find it and the composer can say which sheet a schedule is for.
 */

/** Where a scheduled table lives. Stored on the schedule's attachments and image blocks. */
export type PageCaptureSpec = {
    /** In-app path with its query string, e.g. `/replenishment?objective=…`. */
    path: string;
    /** The sheet's registration key on that page (its email name; `#2`… for repeats). */
    sheetKey: string;
    /**
     * The page's localStorage view state (quick lists, tabs, column picks) as the operator had
     * it when scheduling; the worker replays it before the page boots, since a fresh headless
     * browser has none. Never the session: see `readViewState`.
     */
    viewState?: Record<string, string>;
};

/** What the worker plants in sessionStorage before the page boots. */
export type HeadlessCaptureJob = {
    page: PageCaptureSpec;
    output: 'image' | 'table';
    rowLimit?: number;
};

/** The slice of DataSheet's capture handle the agent calls; restated so DataSheet can import this file without a cycle. */
type SheetCaptureHandle = {
    captureViewImage(options?: { maxVisualRows?: number }): Promise<{ dataUrl: string }>;
    captureViewData(options?: { maxVisualRows?: number }): Promise<unknown>;
};

export type CaptureSheetEntry = {
    handle: () => SheetCaptureHandle | null;
    isLoading: () => boolean;
    rowCount: () => number;
};

export const HEADLESS_CAPTURE_STORAGE_KEY = 'reparo:headless-capture';

const sheets = new Map<string, CaptureSheetEntry>();

/** Registers a sheet; a second sheet with the same name on the page gets ` #2`, and so on. */
export function registerCaptureSheet(name: string, entry: CaptureSheetEntry): { key: string; unregister: () => void } {
    let key = name;
    for (let n = 2; sheets.has(key); n++) key = `${name} #${n}`;
    sheets.set(key, entry);
    return {
        key,
        unregister: () => {
            if (sheets.get(key) === entry) sheets.delete(key);
        },
    };
}

export const getCaptureSheet = (key: string): CaptureSheetEntry | undefined => sheets.get(key);

export const captureSheetKeys = (): string[] => [...sheets.keys()];

/** Never carried into a schedule: the worker plants the owner's own token. The server drops the same keys. */
const VIEW_STATE_EXCLUDED = /token|auth|secret|password|session|^user$/i;
const VIEW_STATE_MAX_VALUE = 20_000;
const VIEW_STATE_MAX_TOTAL = 200_000;

/** This browser's localStorage view state, minus credentials and oversized entries. */
export function readViewState(): Record<string, string> {
    const out: Record<string, string> = {};
    try {
        let total = 0;
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (!key || VIEW_STATE_EXCLUDED.test(key)) continue;
            const value = window.localStorage.getItem(key);
            if (value == null || value.length > VIEW_STATE_MAX_VALUE || total + key.length + value.length > VIEW_STATE_MAX_TOTAL) continue;
            out[key] = value;
            total += key.length + value.length;
        }
    } catch {
        // Storage blocked: the schedule reopens the page with its defaults.
    }
    return out;
}

/** An in-app path only: one leading slash, never a protocol-relative or backslash URL. */
export const isSafeAppPath = (path: unknown): path is string => typeof path === 'string' && /^\/(?![/\\])/.test(path) && !path.includes('\\') && path.length <= 2000;

/** The page the user is on, as a schedule should reopen it. */
export const currentAppPath = (): string => `${window.location.pathname}${window.location.search}`;

export function readHeadlessCaptureJob(): HeadlessCaptureJob | null {
    try {
        const raw = window.sessionStorage.getItem(HEADLESS_CAPTURE_STORAGE_KEY);
        if (!raw) return null;
        const job = JSON.parse(raw) as HeadlessCaptureJob;
        return job?.page?.sheetKey && (job.output === 'image' || job.output === 'table') ? job : null;
    } catch {
        return null;
    }
}

/** Is this page load a headless capture? Pages use it to skip what only a person needs. */
export const isHeadlessCapture = (): boolean => readHeadlessCaptureJob() !== null;
